import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usersAPI } from '../../services/api';

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetting, setResetting] = useState(false);

  const load = () => {
    const params = {};
    if (roleFilter) params.role = roleFilter;
    usersAPI.list(params).then(({ data }) => setUsers(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [roleFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleUser = async (user) => {
    if (user.is_active) {
      await usersAPI.deactivate(user.id);
      setActionMsg(`${user.full_name} deactivated`);
    } else {
      await usersAPI.activate(user.id);
      setActionMsg(`${user.full_name} activated`);
    }
    setTimeout(() => setActionMsg(''), 3000);
    load();
  };

  const openReset = (user) => {
    setResetUser(user);
    setNewPassword('');
    setResetError('');
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetting(true);
    try {
      await usersAPI.resetPassword(resetUser.id, { new_password: newPassword });
      setActionMsg(`Password reset for ${resetUser.full_name}`);
      setTimeout(() => setActionMsg(''), 3000);
      setResetUser(null);
      setNewPassword('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setResetError(Array.isArray(detail) ? detail[0]?.msg || 'Invalid password' : detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const filtered = users.filter(
    (u) => u.full_name.toLowerCase().includes(search.toLowerCase()) ||
           u.username.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="page">
      <div className="page-header">
        <h2>User Management</h2>
        <Link to="/admin/users/new" className="btn btn-primary">+ Create User</Link>
      </div>

      {actionMsg && <div className="alert alert-success">{actionMsg}</div>}

      <div className="filter-bar">
        <input
          type="search"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="">All Roles</option>
          <option value="employee">Employee</option>
          <option value="hr">HR</option>
          <option value="super_admin">Super Admin</option>
        </select>
      </div>

      {loading ? <div className="page-loading">Loading...</div> : (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th><th>Username</th><th>Email</th><th>Role</th>
                <th>Department</th><th>Emp ID</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-muted">No users found</td></tr>
              ) : filtered.map((u) => (
                <tr key={u.id}>
                  <td>{u.full_name}</td>
                  <td className="text-muted">@{u.username}</td>
                  <td>{u.email}</td>
                  <td>
                    <span className={`badge badge-${u.role === 'super_admin' ? 'purple' : u.role === 'hr' ? 'blue' : 'green'}`}>
                      {u.role.replace('_', ' ')}
                    </span>
                  </td>
                  <td>{u.department || '—'}</td>
                  <td>{u.employee_id || '—'}</td>
                  <td>
                    <span className={`badge badge-${u.is_active ? 'green' : 'red'}`}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm btn-ghost" onClick={() => navigate(`/admin/users/${u.id}/edit`)}>Edit</button>
                    <button className="btn btn-sm btn-ghost" onClick={() => openReset(u)}>Reset Password</button>
                    <button
                      className={`btn btn-sm ${u.is_active ? 'btn-danger' : 'btn-success'}`}
                      onClick={() => toggleUser(u)}
                    >
                      {u.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {resetUser && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => !resetting && setResetUser(null)}
        >
          <div className="form-card" style={{ width: 420, maxWidth: '90%' }} onClick={(e) => e.stopPropagation()}>
            <h4>Reset Password</h4>
            <p className="text-muted" style={{ marginTop: 0 }}>
              Set a new password for <strong>{resetUser.full_name}</strong> (@{resetUser.username}).
            </p>
            {resetError && <div className="alert alert-danger">{resetError}</div>}
            <form onSubmit={submitReset}>
              <div className="form-group">
                <label>New Password *</label>
                <input
                  type="password"
                  required
                  autoFocus
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 8 chars, upper, lower, digit, special"
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setResetUser(null)} disabled={resetting}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={resetting}>
                  {resetting ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
